import React, { useEffect, useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Bell, LogOut, LayoutGrid, Users, CheckSquare, Mic, ClipboardCheck, Menu, X as CloseIcon, Sparkles, Radio, KeyRound, Bot } from 'lucide-react';
import NotificationsDock from '@/components/NotificationsDock';
import { CommsProvider, useComms } from '@/context/CommsContext';
import { removePushSubscription } from '@/utils/pushClient';
import { ProfileMenu, startGoogleLink } from '@/components/ProfileMenu';
import { SetPasswordDialog } from '@/components/SetPasswordDialog';
import { ChangePasswordDialog } from '@/components/ChangePasswordDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard({ user, setUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [teams, setTeams] = useState([]);
  const [isOwner, setIsOwner] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [mobilePasswordOpen, setMobilePasswordOpen] = useState(false);
  const [mobileChangeOpen, setMobileChangeOpen] = useState(false);

  useEffect(() => {
    fetchTeams();
    fetchUnreadCount();
    
    // Poll for new notifications every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
      const ownerTeams = response.data.filter(t => t.owner_id === user.id);
      setIsOwner(ownerTeams.length > 0);
    } catch (error) {
      console.error('Failed to fetch teams');
    }
  };

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const unread = response.data.filter(n => !n.read).length;
      setUnreadCount(unread);
    } catch (error) {
      console.error('Failed to fetch notifications count');
    }
  };

  const handleLogout = async () => {
    try {
      const sessionToken = localStorage.getItem('session_token');
      await removePushSubscription();
      await axios.post(`${API}/auth/logout`, {}, {
        headers: { Authorization: `Bearer ${sessionToken}` }
      });
      localStorage.removeItem('session_token');
      setUser(null);
      navigate('/auth/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const baseTabs = [
    { path: '/dashboard/kanban', label: 'Kanban', icon: LayoutGrid, testId: 'nav-kanban' },
    { path: '/dashboard/team', label: 'Team', icon: Users, testId: 'nav-team' },
    { path: '/dashboard/tasks', label: 'Tasks', icon: CheckSquare, testId: 'nav-tasks' },
    { path: '/dashboard/meetings', label: 'Meetings', icon: Mic, testId: 'nav-meetings' },
    { path: '/dashboard/communications', label: 'Communications', icon: Radio, testId: 'nav-comms' },
    { path: '/dashboard/ai-assistant', label: 'AI Assistant', icon: Sparkles, testId: 'nav-ai' },
    { path: '/dashboard/chat', label: 'Chat', icon: Bot, testId: 'nav-chat' }
  ];

  const tabs = isOwner 
    ? [...baseTabs, { path: '/dashboard/approvals', label: 'Approvals', icon: ClipboardCheck, testId: 'nav-approvals' }]
    : baseTabs;

  const handleTabClick = (path) => {
    navigate(path);
  };

  return (
    <CommsProvider user={user}>
    <div className="dashboard-revamped" data-testid="dashboard">
      {/* Top Header Bar */}
      <header className="main-header">
        <div className="header-container">
          {/* Logo */}
          <div className="app-logo-container">
            <img src="/vibely-logo.svg" alt="Vibely" className="app-logo" data-testid="app-logo" />
          </div>
          
          {/* Desktop Navigation Tabs */}
          <nav className="desktop-navigation desktop-only">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = location.pathname === tab.path;
              return (
                <button
                  key={tab.path}
                  onClick={() => handleTabClick(tab.path)}
                  className={`header-nav-tab ${isActive ? 'active' : ''}`}
                  data-testid={tab.testId}
                >
                  <Icon size={20} />
                  <span>{tab.label}</span>
                  {tab.path === '/dashboard/communications' && <CommsNavBadge testId="nav-comms-badge" />}
                </button>
              );
            })}
          </nav>
          
          <div className="header-actions">
            <div className="desktop-only">
              <ProfileMenu user={user} setUser={setUser} onLogout={handleLogout} />
            </div>
            
            <button 
              className="icon-button notification-btn" 
              onClick={() => setShowNotifications(!showNotifications)}
              data-testid="notification-button"
            >
              <Bell size={20} />
              {unreadCount > 0 && (
                <span className="notification-count">{unreadCount}</span>
              )}
            </button>
            
            {/* Mobile Menu Button */}
            <button 
              className="mobile-menu-btn mobile-only"
              data-testid="mobile-menu-button"
              onClick={() => setShowMobileMenu(!showMobileMenu)}
            >
              {showMobileMenu ? <CloseIcon size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Navigation Menu */}
      {showMobileMenu && (
        <div className="mobile-navigation">
          <div className="mobile-user-section">
            <img src={user.picture} alt={user.name} className="profile-avatar" />
            <span className="profile-name">{user.name}</span>
          </div>
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = location.pathname === tab.path;
            return (
              <button
                key={tab.path}
                onClick={() => {
                  handleTabClick(tab.path);
                  setShowMobileMenu(false);
                }}
                className={`mobile-nav-tab ${isActive ? 'active' : ''}`}
                data-testid={`mobile-${tab.testId}`}
              >
                <Icon size={22} />
                <span>{tab.label}</span>
                {tab.path === '/dashboard/communications' && <CommsNavBadge testId="mobile-nav-comms-badge" />}
              </button>
            );
          })}
          {user.google_email ? (
            <div className="mobile-google-label" data-testid="mobile-google-connected-label">Google connected · {user.google_email}</div>
          ) : (
            <button className="mobile-nav-tab" onClick={startGoogleLink} data-testid="mobile-connect-google-button">
              <Sparkles size={22} />
              <span>Connect Google account</span>
            </button>
          )}
          {!user.has_password && (
            <button className="mobile-nav-tab" onClick={() => { setShowMobileMenu(false); setMobilePasswordOpen(true); }} data-testid="mobile-set-password-button">
              <KeyRound size={22} />
              <span>Set a password</span>
            </button>
          )}
          {user.has_password && (
            <button className="mobile-nav-tab" onClick={() => { setShowMobileMenu(false); setMobileChangeOpen(true); }} data-testid="mobile-change-password-button">
              <KeyRound size={22} />
              <span>Change password</span>
            </button>
          )}
          <button 
            className="mobile-logout-btn" 
            onClick={handleLogout}
            data-testid="mobile-logout-button"
          >
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      )}
      <SetPasswordDialog open={mobilePasswordOpen} onOpenChange={setMobilePasswordOpen} user={user} onSaved={setUser} />
      <ChangePasswordDialog open={mobileChangeOpen} onOpenChange={setMobileChangeOpen} user={user} onSaved={setUser} />

      {/* Content Area */}
      <main className="main-content-area">
        <Outlet />
      </main>
      
      {showNotifications && (
        <NotificationsDock 
          onClose={() => setShowNotifications(false)} 
          setUnreadCount={setUnreadCount}
        />
      )}
    </div>
    </CommsProvider>
  );
}

const CommsNavBadge = ({ testId }) => {
  const { totalUnread } = useComms();
  if (!totalUnread) return null;
  return <span className="nav-badge" data-testid={testId}>{totalUnread > 99 ? '99+' : totalUnread}</span>;
};
